import React, { useEffect, useMemo, useState } from 'react';
import { FaLinkedin } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Apis from '../../../../../api';
import { HEADERMENU_PATH } from '../../../../../routes/RoutesURL';
import { RootState } from '../../../../../store/rootReducer';
import HSButton from '../../../../common/Button';
import NormalInput from '../../../../common/NormalInput';
import PhoneNumber, { PhoneNumberInput } from '../../../../common/PhoneNumber';
import LoginActions from '../../../../login/redux/actions';
import { UserStatus } from '../../../../login/utils/Status';
import MessageActions from '../../../../message/redux/actions';
import { ONBOARDING_PATH } from '../../Onbording';
import OnboardingRightContainer from '../common/Container';

type UserProfileProps = {
    isSetting?: boolean;
    onClickCancel?: () => void;
};

const linkedinUrlReg = /^(http(s)?:\/\/)?([\w]+\.)?linkedin\.com\/(pub|in|profile)/gm;

const UserProfile: React.FC<UserProfileProps> = ({ isSetting, onClickCancel }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const uuid = useSelector((state: RootState) => state.user.uuid);
    const user = useSelector((state: RootState) => state.user);

    const [formFields, setFormFields] = useState<FormField>({
        name: undefined,
        linkedinUrl: undefined,
        phoneNumber: {
            countryCode: '+65',
            number: undefined,
        },
        isValidate: false,
    });
    const [loader, setLoader] = useState(false);

    //Load old data
    useEffect(() => {
        if (user.user) {
            setFormFields({
                name: user.user?.userProfile?.name,
                phoneNumber: {
                    countryCode: user.user?.userProfile?.phoneNumber?.countryCode || '+65',
                    number: user.user?.userProfile?.phoneNumber?.number,
                },
                linkedinUrl: user.user.userProfile?.linkedInProfile,
                isValidate: false,
            });
        }
        return () => {
            setFormFields({
                name: undefined,
                linkedinUrl: undefined,
                phoneNumber: {
                    countryCode: '+65',
                    number: undefined,
                },
                isValidate: false,
            });
        };
    }, [user.user]);

    const form = useMemo(() => {
        const onFieldChange = (data: { name: string; value?: number | string | PhoneNumberInput }) => {
            const { name, value } = data;
            let obj = { ...formFields };
            if (name) {
                obj[name] = (value as string) || '';
            }
            setFormFields(obj);
        };
        return (
            <>
                <div className="mb-4">
                    <NormalInput
                        name="name"
                        title="Name"
                        isRequired
                        value={formFields.name}
                        onChange={onFieldChange}
                        className="rounded-xl"
                    />
                    {formFields.isValidate && (!formFields.name || formFields.name.trim() === '') && (
                        <span className="common_error">Please enter name</span>
                    )}
                </div>
                <div className="mb-4">
                    <PhoneNumber
                        name="phoneNumber"
                        title="Mobile Number"
                        value={formFields.phoneNumber}
                        isRequired
                        onChange={onFieldChange}
                        className="common_radius"
                    />
                    {formFields.isValidate &&
                        (!formFields.phoneNumber ||
                            formFields.phoneNumber.countryCode === '' ||
                            !formFields.phoneNumber.number) && (
                            <span className="common_error">Please enter phone number</span>
                        )}
                </div>
                <div className="mb-4">
                    <div className="h-4" />
                    <NormalInput
                        name="linkedinUrl"
                        title="LinkedIn Profile"
                        value={formFields.linkedinUrl}
                        onChange={onFieldChange}
                        placeholder="https://www.linkedin.com/in/"
                        suffix={<FaLinkedin color="#0A66C2" size={'1.4rem'} />}
                    />
                    {formFields.isValidate &&
                        formFields.linkedinUrl &&
                        formFields.linkedinUrl.trim() !== '' &&
                        !formFields.linkedinUrl.match(linkedinUrlReg) && (
                            <span className="common_error">Please enter correct linkedin url</span>
                        )}
                </div>
            </>
        );
    }, [formFields]);

    const updateUserProfile = () => {
        setFormFields({ ...formFields, isValidate: true });
        if (
            !formFields.name ||
            formFields.name.trim() === '' ||
            !formFields.phoneNumber ||
            formFields.phoneNumber.countryCode.trim() === '' ||
            !formFields.phoneNumber.number
        ) {
            return;
        }
        if (formFields.linkedinUrl && formFields.linkedinUrl.trim() !== '') {
            if (formFields.linkedinUrl.match(linkedinUrlReg)) {
            } else {
                return;
            }
        }
        setFormFields({ ...formFields, isValidate: false });
        if (uuid) {
            setLoader(true);
            Apis.updateUserProfile(uuid, {
                name: formFields.name,
                phoneNumber: formFields.phoneNumber,
                linkedInProfile: formFields.linkedinUrl,
            })
                .then(({ data }) => {
                    // if (isSetting) {
                    //     dispatch(
                    //         MessageActions.showMessage({ error: false, text: 'User profile updated successfully!' }),
                    //     );
                    // }
                    dispatch(
                        LoginActions.setUserData({
                            ...user,
                            user: data,
                        }),
                    );
                    if (!isSetting) {
                        if (user.user?.status === UserStatus.pendingActivation) {
                            navigate(HEADERMENU_PATH.accountActivationWithOtp);
                            return;
                        }
                        navigate(`${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.ACCREDITATION}`);
                    }
                    if (isSetting && onClickCancel) {
                        onClickCancel();
                    }
                })
                .catch((err) => {
                    dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };

    return (
        <OnboardingRightContainer id="profile">
            <p className="page_title_onboarding pb-3">Enter Basic Details</p>
            {form}
            <div className="flex justify-center items-center mt-10 mb-5">
                <HSButton
                    title={isSetting ? 'Update' : 'Next'}
                    onClick={updateUserProfile}
                    type="primary"
                    loading={loader}
                />
                {isSetting && onClickCancel && (
                    <HSButton
                        className="onboarding_btn ml-2"
                        disabled={loader}
                        light
                        title="Cancel"
                        onClick={onClickCancel}
                    />
                )}
            </div>
        </OnboardingRightContainer>
    );
};

export default UserProfile;

interface FormField {
    [k: string]: string | PhoneNumberInput | boolean | undefined;
    linkedinUrl?: string;
    name?: string;
    phoneNumber: PhoneNumberInput;
    isValidate: boolean;
}
